import { useEffect, useRef } from 'react'
import { normalizeLocale, type Locale } from '../../i18n'
import type { AdminAppState } from '../useAdminApp'

type SetupStatus = {
  firstInstallCompleted: boolean
} | null

export const isCustomerOnlyRoles = (roles: string[] | undefined) =>
  Array.isArray(roles) &&
  roles.includes('customer') &&
  !roles.some((role) => role === 'admin' || role === 'super-admin' || role === 'site-manager')

export const useAdminAppEffects = ({
  app,
  setupStatus,
  setupLoading,
  refreshStatus,
  logout,
  language,
  setLanguage,
}: {
  app: AdminAppState
  setupStatus: SetupStatus
  setupLoading: boolean
  refreshStatus: () => Promise<void>
  logout: () => Promise<void>
  language: Locale
  setLanguage: (language: Locale) => void
}) => {
  const lastProfileLanguageRef = useRef<Locale | null>(null)
  const customerRedirectingRef = useRef(false)
  const profileLanguage = app.profile?.language
  const accountRoles = app.account?.roles
  const isAuthenticated = app.isAuthenticated
  const appLogout = app.logout

  useEffect(() => {
    void refreshStatus()
  }, [refreshStatus])

  useEffect(() => {
    if (setupStatus && !setupStatus.firstInstallCompleted && isAuthenticated) {
      void logout()
    }
  }, [isAuthenticated, logout, setupStatus])

  useEffect(() => {
    if (setupLoading) return
    const normalized = normalizeLocale(profileLanguage ?? null)
    if (!normalized) return
    if (lastProfileLanguageRef.current !== normalized) {
      lastProfileLanguageRef.current = normalized
      if (language !== normalized) {
        setLanguage(normalized)
      }
    }
  }, [language, profileLanguage, setLanguage, setupLoading])

  useEffect(() => {
    if (!isAuthenticated) return
    if (!isCustomerOnlyRoles(accountRoles)) return
    if (customerRedirectingRef.current) return
    customerRedirectingRef.current = true

    void appLogout().finally(() => {
      if (typeof window !== 'undefined') {
        window.location.replace('/dashboard')
      }
    })
  }, [accountRoles, appLogout, isAuthenticated])
}
